import { dateFnsLocalizer } from 'react-big-calendar';
import {
  addDays,
  addMinutes,
  addWeeks,
  endOfDay,
  format,
  getDay,
  getMinutes,
  isAfter,
  isBefore,
  isEqual,
  isValid,
  parse,
  setDay,
  startOfWeek,
  transpose,
} from 'date-fns';
import { enUS } from 'date-fns/locale';
import type { LocalDate, LocalDateTime, LocalTime } from '@zg-rentals/ts-z-calendar-client';
import { tz, TZDate } from '@date-fns/tz';
import type { TourDuration } from '@zg-rentals/ts-z-calendar-client/src/types/zcalendar/TourDuration';

import {
  type AvailabilityEventWeeklyRecurringDaysMap,
  CALENDAR_EVENT_DATE_DISPLAY_FORMAT_STRING,
  HOUR_FORMAT_STRING,
  HOUR_MERIDIEM_FORMAT_STRING,
  HOUR_MINUTE_FORMAT_STRING,
  HOUR_MINUTE_MERIDIEM_FORMAT_STRING,
  type TimeZoneIds,
  TOUR_DURATION_NUMBER_MAP,
  TWENTY_FOUR_HOUR_FORMAT_STRING,
  WeekdayToNumber,
  type WeeklyRecurringDays,
  type ZCalendarEvent,
} from '../constants/TourCalendar';

export const createDateFnsLocalizer = () => {
  const locales = {
    'en-US': enUS,
  };

  const localizer = dateFnsLocalizer({
    format,
    parse,
    startOfWeek,
    getDay,
    locales,
  });

  return localizer;
};

export const getMaxDateForRecurringEvents = (date: Date) => addWeeks(date, 3);

export type GetTimeFormatStringOptions = {
  showMeridiem?: boolean;
  truncateZeroMinutes?: boolean;
};
export const getTimeFormatString = (date: Date, options?: GetTimeFormatStringOptions) => {
  const showMeridiem = typeof options?.showMeridiem === 'boolean' ? options.showMeridiem : false;

  const minutes = getMinutes(date);

  if (showMeridiem) {
    return minutes === 0 && options?.truncateZeroMinutes
      ? HOUR_MERIDIEM_FORMAT_STRING
      : HOUR_MINUTE_MERIDIEM_FORMAT_STRING;
  }

  return minutes === 0 && options?.truncateZeroMinutes ? HOUR_FORMAT_STRING : HOUR_MINUTE_FORMAT_STRING;
};

export const formatTimeString = (date: Date, options?: GetTimeFormatStringOptions) => {
  return format(date, getTimeFormatString(date, options)).toLowerCase();
};

const formatTimeRangeString = (startDate: Date, endDate: Date) => {
  const showStartTimeMeridiem = startDate.getHours() < 12 && endDate.getHours() >= 12;
  const startTime = formatTimeString(startDate, { truncateZeroMinutes: true, showMeridiem: showStartTimeMeridiem });
  const endTime = formatTimeString(endDate, { truncateZeroMinutes: true, showMeridiem: true });

  return `${startTime}-${endTime}`;
};

export const getTourEventTimeRange = (event: ZCalendarEvent) => {
  if (!isValid(event.start) || !isValid(event.end)) {
    return null;
  }

  return formatTimeRangeString(event.start, event.end);
};

export const formatTourTimeRange = (startDateTime: LocalDateTime, endDateTime: LocalDateTime) => {
  const startDate = convertLocalDateTimeToDate(startDateTime);
  const endDate = convertLocalDateTimeToDate(endDateTime);

  return formatTimeRangeString(startDate, endDate);
};

export const getTourDateAndTimeRange = (startDateTime: LocalDateTime, endDateTime: LocalDateTime) => {
  const tourStart = convertLocalDateTimeToDate(startDateTime);
  const tourTimeSlot = formatTourTimeRange(startDateTime, endDateTime);
  return `${format(tourStart, 'EEE, MMM d')}, ${tourTimeSlot}`;
};

export const setTimeForDate = (date: Date, time: string) => {
  const dateClone = new Date(date.getTime());
  dateClone.setHours(parseInt(time.split(':')[0]), parseInt(time.split(':')[1]));

  return dateClone;
};

export const convertDateToLocalDate = (date: Date): LocalDate => {
  return {
    year: date.getFullYear(),
    month: date.getMonth() + 1,
    day: date.getDate(),
  };
};

export const convertDateToLocalDateTime = (date: Date): LocalDateTime => {
  return {
    date: convertDateToLocalDate(date),
    time: {
      hour: date.getHours(),
      minute: date.getMinutes(),
      second: date.getSeconds(),
      nano: date.getMilliseconds() * 1000000,
    },
  };
};

export const convertLocalDateTimeToDate = (localDateTime: LocalDateTime): Date =>
  new Date(
    localDateTime.date.year,
    localDateTime.date.month - 1,
    localDateTime.date.day,
    localDateTime.time.hour,
    localDateTime.time.minute,
  );

export const convertLocalTimeToDate = (localTime: LocalTime): Date => {
  const date = new Date();
  date.setHours(localTime.hour, localTime.minute);
  return date;
};

export const convert12HourTimeToLocalTime = (time: string): LocalTime => {
  const timeFormat = time.includes(':') ? HOUR_MINUTE_MERIDIEM_FORMAT_STRING : HOUR_MERIDIEM_FORMAT_STRING;
  const date = parse(time, timeFormat, new Date());

  return {
    hour: date.getHours(),
    minute: date.getMinutes(),
    second: 0,
    nano: 0,
  };
};

const getRecurrenceEndDate = (recurrenceEndDateMs: number | undefined) => {
  if (recurrenceEndDateMs) {
    return format(new Date(recurrenceEndDateMs), CALENDAR_EVENT_DATE_DISPLAY_FORMAT_STRING);
  }

  return 'listing is deactivated';
};

export const getDailyRecurrenceString = (recurrenceEndDateMs: number | undefined) => {
  return `Every day until ${getRecurrenceEndDate(recurrenceEndDateMs)}`;
};

export const getWeeklyRecurrenceString = (
  recurringDays: AvailabilityEventWeeklyRecurringDaysMap,
  recurrenceEndDateMs: number | undefined,
) => {
  const recurringDayNames = Object.entries(recurringDays)
    .filter(([_, isSelected]) => isSelected)
    .map(([day, _]) => {
      const date = new Date();
      return format(setDay(date, WeekdayToNumber[day as WeeklyRecurringDays]), 'EEEE');
    });

  if (recurringDayNames.length === 0) {
    return '';
  }

  if (recurringDayNames.length === 1) {
    return `Every week on ${recurringDayNames[0]} until ${getRecurrenceEndDate(recurrenceEndDateMs)}`;
  }

  if (recurringDayNames.length === 2) {
    return `Every week on ${recurringDayNames[0]} and ${recurringDayNames[1]} until ${getRecurrenceEndDate(recurrenceEndDateMs)}`;
  }

  const lastDay = recurringDayNames.pop();
  return `Every week on ${recurringDayNames.join(', ')}, and ${lastDay} until ${getRecurrenceEndDate(recurrenceEndDateMs)}`;
};

export const getToastDisplayDate = (date: Date) => {
  const formattedStartTime = formatTimeString(date, { showMeridiem: true, truncateZeroMinutes: true });
  const formattedDate = format(date, 'EEE, MMM d');

  return `${formattedDate} at ${formattedStartTime}`;
};

export const getEventToastDisplayDate = (event: ZCalendarEvent) => {
  if (!isValid(event.start)) {
    return '';
  }
  return getToastDisplayDate(event.start);
};

export const getTourDateAndTime = (startDate: Date, endDate: Date) => {
  if (!isValid(startDate) || !isValid(endDate)) {
    return '';
  }
  const timeRange = formatTimeRangeString(startDate, endDate);
  const formattedDate = format(startDate, 'EEE, MMM d');
  return `${formattedDate}, ${timeRange}`;
};

export const getAddToYourAvailabilityDates = (tourDuration: TourDuration, date: Date = new Date()) => {
  const tomorrowDate = addDays(date, 1);
  const tomorrowDateAtNineAM = setTimeForDate(tomorrowDate, '09:00');
  return {
    start: tomorrowDateAtNineAM,
    end: addMinutes(tomorrowDateAtNineAM, TOUR_DURATION_NUMBER_MAP[tourDuration]),
  };
};

// The time of date is assumed to be selected in tour config timezone so no need to convert to tour config timezone
// But new Date() is in the client's local timezone, so needs to be converted to tour config timezone
export const isDatePastByTimeZone = (date: Date, timeZone: TimeZoneIds) =>
  isBefore(transpose(date, tz(timeZone)), new TZDate(new Date(), timeZone));

export const isDateTooFarInAdvance = (date: Date, timeZone: TimeZoneIds) =>
  isAfter(transpose(date, tz(timeZone)), addWeeks(endOfDay(new TZDate(new Date(), timeZone)), 3));

export const isUpdatedDateInPast = ({
  existingDate,
  updatedDate,
  timeZone,
}: {
  existingDate: Date;
  updatedDate: Date;
  timeZone: TimeZoneIds;
}) => {
  // If updatedDate is unchanged, it's valid. Allows events with start times in the past to be updateable/extended
  if (isEqual(existingDate, updatedDate)) {
    return false;
  }

  return isDatePastByTimeZone(updatedDate, timeZone);
};

export const createCalendarTimeOptions = () => {
  const times = [];
  for (let hour = 0; hour < 24; hour++) {
    times.push(`${hour}:00`);
  }

  return times.map((time) => {
    return formatTimeString(parse(time, TWENTY_FOUR_HOUR_FORMAT_STRING, new Date()), { showMeridiem: true });
  });
};
